import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiService } from '../../services/api';
import { Trophy, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AdminStandings = () => {
  const [standings, setStandings] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('U-8');
  const [eventCategories, setEventCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadEvents();
  }, []);

  useEffect(() => {
    if (!selectedEventId) return;
    loadStandings();
  }, [selectedEventId, categoryFilter]);

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const response = await apiService.getPublicEvents();
      const list = response.data || [];
      setEvents(list);
      if (list.length > 0) {
        const preferredEvent = list.find((eventItem) =>
          (eventItem?.name || '').toLowerCase().includes('test event')
        );
        const defaultEvent = preferredEvent || list[0];
        setSelectedEventId(defaultEvent.id);
        const categories = defaultEvent.categories || [];
        setEventCategories(categories);
        if (categories.length > 0) setCategoryFilter(categories[0]);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Gagal memuat event', variant: 'destructive' });
    } finally {
      setEventsLoading(false);
    }
  };

  const handleChangeEvent = (eventId) => {
    setSelectedEventId(eventId);
    const selectedEvent = events.find((eventItem) => eventItem.id === eventId);
    const categories = selectedEvent?.categories || [];
    setEventCategories(categories);
    if (categories.length > 0 && !categories.includes(categoryFilter)) {
      setCategoryFilter(categories[0]);
    }
  };

  const loadStandings = async () => {
    setLoading(true);
    try {
      const response = await apiService.getStandings(categoryFilter, selectedEventId);
      setStandings(response.data || []);
    } catch (error) {
      setStandings([]);
      toast({ title: 'Error', description: 'Gagal memuat klasemen', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (eventsLoading) {
    return (
      <AdminLayout>
        <div className="py-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-green-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-bold">Klasemen (Live dari Match Event)</h2>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Event</label>
            <select
              value={selectedEventId}
              onChange={(e) => handleChangeEvent(e.target.value)}
              className="w-full px-4 py-2 border rounded-md"
            >
              {events.map((eventItem) => (
                <option key={eventItem.id} value={eventItem.id}>{eventItem.name || eventItem.id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Kategori</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-4 py-2 border rounded-md"
            >
              {(eventCategories.length > 0 ? eventCategories : ['U-8', 'U-10', 'U-12', 'U-14']).map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Memuat klasemen...</div>
        ) : standings.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500">
            Belum ada data klasemen untuk event dan kategori ini.
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Pos</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Tim</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Main</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">M</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">S</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">K</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">GM</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">GK</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">SG</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Poin</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {standings.map((s) => (
                  <tr key={s.id}>
                    <td className="px-4 py-3 flex items-center">
                      <span className="font-bold mr-2">{s.position}</span>
                      {s.position <= 3 && <Trophy className="h-4 w-4 text-yellow-500" />}
                    </td>
                    <td className="px-4 py-3 font-semibold">{s.team}</td>
                    <td className="px-4 py-3 text-center">{s.played}</td>
                    <td className="px-4 py-3 text-center">{s.won}</td>
                    <td className="px-4 py-3 text-center">{s.drawn}</td>
                    <td className="px-4 py-3 text-center">{s.lost}</td>
                    <td className="px-4 py-3 text-center">{s.goals_for}</td>
                    <td className="px-4 py-3 text-center">{s.goals_against}</td>
                    <td className="px-4 py-3 text-center">{s.goal_difference}</td>
                    <td className="px-4 py-3 text-center font-bold text-green-600">{s.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminStandings;
